import { useEffect, useState } from "react";
import { getBillingConfig, getSubscription, getBillingHistory, trialInit, subscribeInit, cancelSubscription, changePlan, apiError, checkoutError, receiptPdfUrl, getAddons, subscribeAddon, cancelAddon } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Loading } from "@/components/Shared";
import { ShareButton } from "@/components/ShareButton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CreditCard, Check, Loader2, RefreshCw, Sparkles, AlertTriangle, Lock } from "lucide-react";

const TEAL = "#1E7A6B";
const ORDER = ["free", "starter", "business", "pro"];

const FEATURES = {
  free: ["Invoices each month: 3", "Quotes each month: 3", "Team members: 1"],
  starter: ["Invoices each month: Unlimited", "Quotes each month: Unlimited", "Team members: 1"],
  business: ["Invoices each month: Unlimited", "Quotes each month: Unlimited", "Professional reports", "Team members: 3"],
  pro: ["Invoices each month: Unlimited", "Quotes each month: Unlimited", "Professional reports", "Team members: 5", "API access"],
};

function RecoveryBanner({ rec, onPay }) {
  if (!rec) return null;
  const banners = [];
  const fmtDays = (d) => d == null ? "" : Math.max(0, Math.ceil(d));
  if (rec.suspended) banners.push({ key: "sub-suspended", locked: true, label: "Subscription suspended",
    msg: "Your subscription renewal payment could not be collected. Access is locked — your data is safe. Update your payment to restore access immediately." });
  else if (rec.in_grace) banners.push({ key: "sub-grace", locked: false, label: "Subscription payment failed",
    msg: `We couldn't process your renewal. Access stays active for ${fmtDays(rec.grace_days_left)} more day(s). Please update your payment to avoid suspension.` });
  if (rec.addon_suspended) banners.push({ key: "addon-suspended", locked: true, label: "Plumbing add-on suspended",
    msg: "Your Plumbing Field Operations renewal could not be collected. The add-on is locked — update your payment to restore it." });
  else if (rec.addon_in_grace) banners.push({ key: "addon-grace", locked: false, label: "Plumbing add-on payment failed",
    msg: `We couldn't process your Plumbing add-on renewal. It stays active for ${fmtDays(rec.addon_grace_days_left)} more day(s). Update your payment to avoid suspension.` });
  if (!banners.length) return null;
  return (
    <div className="space-y-3 mb-6" data-testid="recovery-banner">
      {banners.map((b) => (
        <div key={b.key} data-testid={`recovery-${b.key}`}
          className={`rounded-2xl border p-4 flex items-start gap-3 ${b.locked ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`}>
          {b.locked ? <Lock className="w-5 h-5 mt-0.5 text-rose-600 shrink-0" /> : <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />}
          <div className="flex-1">
            <div className={`font-semibold text-sm ${b.locked ? "text-rose-700" : "text-amber-800"}`}>{b.label}</div>
            <div className={`text-sm mt-0.5 ${b.locked ? "text-rose-600" : "text-amber-700"}`}>{b.msg}</div>
          </div>
          <Button size="sm" onClick={onPay} data-testid={`recovery-pay-${b.key}`}
            className={b.locked ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}>
            Update payment
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function Billing() {  const [cfg, setCfg] = useState(null);
  const [sub, setSub] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [checkout, setCheckout] = useState(null); // {plan}
  const [manage, setManage] = useState(false);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const load = () => Promise.all([getBillingConfig(), getSubscription()]).then(([c, s]) => { setCfg(c); setSub(s); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openManage = async () => { try { setHistory(await getBillingHistory()); setManage(true); } catch (e) { toast.error(apiError(e)); } };

  const statusLabel = (st) => ({ trial_active: "Trial Active", active: "Active", trial_pending: "Trial payment required", payment_failed: "Payment failed", cancelled: "Cancelled", free: "Free plan", past_due: "Past due", expired: "Expired" }[st] || st);

  const startCheckout = async () => {
    if (!checkout) return;
    setBusy(true);
    try {
      const useTrial = !sub.trial_used;
      const res = useTrial ? await trialInit(checkout.plan, cycle) : await subscribeInit(checkout.plan, cycle);
      if (!res?.authorization_url) throw new Error("Invalid checkout response");
      window.location.href = res.authorization_url;
    } catch (e) { toast.error(checkoutError(e)); setBusy(false); }
  };

  const retryTrial = async () => {
    setBusy(true);
    try {
      const plan = sub.plan && sub.plan !== "free" ? sub.plan : "business";
      const res = await trialInit(plan, sub.billing_cycle || "monthly");
      if (!res?.authorization_url) throw new Error("Invalid checkout response");
      window.location.href = res.authorization_url;
    } catch (e) { toast.error(checkoutError(e)); setBusy(false); }
  };

  const doCancel = async () => { try { const s = await cancelSubscription(); setSub(s); toast.success("Subscription cancelled"); setManage(false); } catch (e) { toast.error(apiError(e)); } };

  if (!cfg || !sub) return <Loading />;

  const price = (plan) => cycle === "annual" ? PLAN_PRICE(cfg, plan, "annual") : PLAN_PRICE(cfg, plan, "monthly");
  const isCurrent = (plan) => sub.plan === plan && ["active", "trial_active"].includes(sub.status);
  const useTrial = !sub.trial_used;

  return (
    <div className="-m-4 md:-m-6 lg:-m-8 p-4 md:p-6 lg:p-8 min-h-screen" style={{ background: "#FBF7EF" }} data-testid="billing-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: TEAL }}>Subscription</div>
        <h1 className="font-display text-3xl font-extrabold text-[#16241F]">Billing &amp; plans</h1>
        <p className="text-slate-500 mt-2 mb-5">Manage your subscription, view plan features, and update payment details.</p>
        <Button onClick={openManage} className="mb-6" style={{ background: "#EAE3D6", color: "#16241F" }} data-testid="manage-billing-button"><CreditCard className="w-4 h-4 mr-2" /> Manage billing</Button>

        <RecoveryBanner rec={sub.recovery} onPay={() => openManage()} />

        {/* current status */}
        <div className="rounded-2xl bg-[#FCFAF4] border border-[#EAE3D6] shadow-sm p-6 mb-8" data-testid="current-status-card">
          <div className="text-xs font-bold tracking-widest uppercase text-slate-500">Current Status</div>
          <div className="flex items-start justify-between mt-2">
            <div>
              <div className="font-display text-3xl font-extrabold text-[#16241F]" data-testid="current-plan-name">{PLAN_NAME(cfg, sub.plan)} Plan</div>
              <div className="mt-3 text-sm text-slate-600">
                {sub.status === "trial_active" && <>Trial payment: R14.00 · Trial started {fmtDate(sub.trial_started_at)}<br/>Next billing: {fmtDate(sub.next_billing_date)}</>}
                {sub.status === "active" && <>Next payment: {money(sub.amount)} · Billing cycle: {sub.billing_cycle}<br/>Next billing date: {fmtDate(sub.next_billing_date)}</>}
                {sub.status === "free" && <>R14.00 trial payment required to start.</>}
                {sub.status === "trial_pending" && <>R14.00 trial payment required.</>}
                {sub.status === "payment_failed" && <span className="text-rose-600">Trial payment was unsuccessful.</span>}
                {sub.status === "cancelled" && <>Access remains until {fmtDate(sub.next_billing_date)}.</>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "#DCEEE4", color: TEAL }} data-testid="subscription-status">{statusLabel(sub.status)}</span>
              <button onClick={load} className="w-9 h-9 rounded-lg border border-[#EAE3D6] flex items-center justify-center text-slate-500 hover:bg-white" data-testid="refresh-billing"><RefreshCw className="w-4 h-4" /></button>
            </div>
          </div>
          {!sub.trial_used && ["trial_pending", "payment_failed", "cancelled", "free"].includes(sub.status) && sub.status !== "free" && (
            <div className="mt-4">
              <Button onClick={retryTrial} disabled={busy} className="text-white h-11 rounded-xl" style={{ background: TEAL }} data-testid="retry-r14-button">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Retry R14 Payment</>}
              </Button>
            </div>
          )}
        </div>

        {/* plans */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-bold text-[#16241F]">Available plans</h2>
          <div className="inline-flex rounded-full bg-[#EAE3D6] p-1" data-testid="billing-cycle-toggle">
            {["monthly", "annual"].map((c) => (
              <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${cycle === c ? "bg-white text-[#16241F] shadow" : "text-slate-500"}`}>
                {c === "monthly" ? "Monthly" : "Annually"}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {ORDER.map((plan) => (
            <div key={plan} className="rounded-2xl bg-[#FCFAF4] border border-[#EAE3D6] shadow-sm p-6" data-testid={`plan-card-${plan}`}>
              {isCurrent(plan) && <div className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: TEAL }}>Current Plan</div>}
              <div className="font-display text-2xl font-extrabold text-[#16241F]">{PLAN_NAME(cfg, plan)}</div>
              <div className="mt-1 mb-4"><span className="font-display text-3xl font-extrabold text-[#16241F]">R {price(plan)}</span><span className="text-slate-400 text-sm"> / {cycle === "annual" ? "yr" : "mo"}</span></div>
              <div className="space-y-2 mb-5">
                {FEATURES[plan].map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-slate-600 text-sm"><Check className="w-4 h-4" style={{ color: TEAL }} /> {f}</div>
                ))}
              </div>
              {plan === "free" ? (
                <Button disabled className="w-full h-12 rounded-xl" style={{ background: "#EAE3D6", color: "#8A8577" }} data-testid="free-included">Included Plan</Button>
              ) : isCurrent(plan) ? (
                <Button disabled className="w-full h-12 rounded-xl" style={{ background: "#EAE3D6", color: "#8A8577" }} data-testid={`current-${plan}`}>Current Plan</Button>
              ) : (
                <Button onClick={() => setCheckout({ plan })} className="w-full h-12 rounded-xl text-white font-semibold" style={{ background: TEAL }} data-testid={`subscribe-${plan}`}>Subscribe to {PLAN_NAME(cfg, plan)}</Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* checkout summary */}
      <Dialog open={!!checkout} onOpenChange={(o) => { if (!o) { setCheckout(null); setAgreed(false); } }}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle>{useTrial ? "Start your WiseAce trial for R14.00" : `Subscribe to ${checkout ? PLAN_NAME(cfg, checkout.plan) : ""}`}</DialogTitle><DialogDescription>{useTrial ? "Pay R14.00 now to start your 14-day trial." : "Review your plan and continue to secure Paystack checkout."}</DialogDescription></DialogHeader>
          {checkout && (
            <div className="space-y-3 text-sm">
              <div className="rounded-xl bg-[#F1F5F9] p-4 space-y-2">
                <Row l="Selected plan" v={`${PLAN_NAME(cfg, checkout.plan)} (${cycle === "annual" ? "Annual" : "Monthly"})`} />
                {useTrial && <Row l="Trial starting fee" v="R14.00" bold />}
                <Row l={useTrial ? "Subscription after trial" : "Amount due now"} v={`R ${price(checkout.plan)} / ${cycle === "annual" ? "yr" : "mo"}`} bold={!useTrial} />
                <Row l="Billing frequency" v={cycle === "annual" ? "Annually (recurring)" : "Monthly (recurring)"} />
                <Row l="Currency" v="ZAR" />
                <Row l="Payment provider" v="Paystack (secure)" />
              </div>
              {useTrial && <p className="text-xs text-slate-500">You pay <b>R14.00</b> now to start your 14-day trial. Recurring billing of R{price(checkout.plan)} begins on your next billing date. The trial can only be used once.</p>}
              <div className="rounded-xl border border-[#EAE3D6] p-3 max-h-36 overflow-y-auto text-xs text-slate-500 space-y-2" data-testid="checkout-policies">
                <div>
                  <div className="font-semibold text-[#16241F]">Terms &amp; Conditions</div>
                  <p>This is a recurring WiseAce Invoicing subscription billed in ZAR via Paystack until cancelled. By continuing you authorise Paystack to charge the amount above on each billing date. Pricing and entitlements follow your selected plan.</p>
                </div>
                <div>
                  <div className="font-semibold text-[#16241F]">Refund &amp; Cancellation Policy</div>
                  <p>You may cancel anytime from Manage billing; cancellation stops future renewals and you keep access until your current paid period ends. The once-off R14 trial fee is non-refundable. Your data and documents are preserved after cancellation.</p>
                </div>
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-600" data-testid="checkout-agree">
                <input type="checkbox" className="mt-0.5" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="checkout-agree-checkbox" />
                <span>I have reviewed the transaction and accept the Terms &amp; Conditions and Refund &amp; Cancellation Policy.</span>
              </label>
              {!cfg.configured && <p className="text-xs text-amber-600">Note: Paystack is not configured yet, so checkout cannot complete until it is set up.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCheckout(null); setAgreed(false); }}>Cancel</Button>
            <Button onClick={startCheckout} disabled={busy || !agreed} className="text-white" style={{ background: TEAL }} data-testid="proceed-checkout">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> {useTrial ? "Pay R14.00 & Start Trial" : "Continue to Paystack"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlumbingAddon />

      {/* manage billing */}
      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent className="max-w-lg" data-testid="manage-dialog">
          <DialogHeader><DialogTitle>Manage billing</DialogTitle><DialogDescription>View your plan, payment history and manage your subscription.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-[#F1F5F9] p-4 text-sm space-y-1.5">
              <Row l="Plan" v={`${PLAN_NAME(cfg, sub.plan)}`} />
              <Row l="Status" v={statusLabel(sub.status)} />
              <Row l="Billing cycle" v={sub.billing_cycle} />
              <Row l="Next billing date" v={fmtDate(sub.next_billing_date)} />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#16241F] mb-2">Payment history</div>
              <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                {history.length === 0 && <div className="text-sm text-slate-400 py-3">No payments yet.</div>}
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between py-2 text-sm" data-testid={`history-${h.id}`}>
                    <div><div className="text-[#16241F]">{h.description}</div><div className="text-xs text-slate-400">{fmtDate(h.created_at)} · {h.reference}</div></div>
                    <div className="text-right flex items-center gap-3">
                      <div><div className="font-semibold text-[#16241F]">{money(h.amount)}</div><div className="text-xs text-emerald-600 capitalize">{h.status}</div></div>
                      {h.status === "successful" && <a href={receiptPdfUrl(h.reference)} download className="text-xs font-semibold underline" style={{ color: TEAL }} data-testid={`download-receipt-${h.id}`}>Receipt</a>}
                      {h.status === "successful" && <span className="ml-2 inline-block"><ShareButton docType="receipt" docId={h.reference} variant="ghost" size="sm" label="Share" /></span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {sub.plan !== "free" && sub.status !== "cancelled" && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" className="w-full text-rose-600 border-rose-200" data-testid="cancel-subscription-button">Cancel subscription</Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Cancel subscription?</AlertDialogTitle><AlertDialogDescription>You'll keep access until {fmtDate(sub.next_billing_date)}.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Keep plan</AlertDialogCancel><AlertDialogAction onClick={doCancel} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-cancel-subscription">Cancel subscription</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, bold }) {
  return <div className="flex justify-between"><span className="text-slate-500">{l}</span><span className={bold ? "font-bold text-[#16241F]" : "text-[#16241F]"}>{v}</span></div>;
}
function PLAN_NAME(cfg, plan) { return cfg.plans[plan]?.name || plan; }
function PLAN_PRICE(cfg, plan, cycle) { const p = cfg.plans[plan]; return (cycle === "annual" ? p?.annual : p?.monthly) ?? 0; }

function PlumbingAddon() {
  const [d, setD] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const load = () => getAddons().then((r) => { setD(r.plumbing); if (r.plumbing.billing_cycle) setCycle(r.plumbing.billing_cycle); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const money2 = (v) => `R${Number(v || 0).toFixed(2)}`;
  const addonAmt = cycle === "annual" ? d.annual_price : d.monthly_price;
  const per = cycle === "annual" ? "/year" : "/month";
  const badge = { active: ["ACTIVE", "bg-emerald-100 text-emerald-700"], locked: ["LOCKED", "bg-slate-100 text-slate-600"], payment_pending: ["PAYMENT PENDING", "bg-amber-100 text-amber-700"], payment_failed: ["PAYMENT FAILED", "bg-rose-100 text-rose-700"], cancelled: ["CANCELLED", "bg-slate-100 text-slate-600"], ineligible: ["REQUIRES BUSINESS/PRO", "bg-rose-50 text-rose-600"] }[d.status] || ["LOCKED", "bg-slate-100"];
  const subscribe = async () => { setBusy(true); try { const r = await subscribeAddon(cycle); if (r?.authorization_url) window.location.href = r.authorization_url; else { toast.error("Could not start payment"); setBusy(false); } } catch (e) { toast.error(checkoutError(e)); setBusy(false); } };
  const doCancel = async () => { try { await cancelAddon(); toast.success("Add-on cancelled. Your data is preserved."); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="mt-8" data-testid="plumbing-addon-card">
      <h2 className="text-lg font-bold text-[#16241F] mb-3">Add-ons</h2>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#16241F]">Plumbing Field Operations</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badge[1]}`} data-testid="addon-status-badge">{badge[0]}</span>
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">Unlimited field employees, teams, job tracking, stock ordering, supplier material ordering & monthly team reports.</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[#16241F]" data-testid="addon-price">{money2(addonAmt)}</div>
            <div className="text-xs text-slate-400">{per}</div>
          </div>
        </div>

        {!d.eligible && (
          <div className="mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3" data-testid="addon-ineligible">Plumbing Field Operations requires a Business or Pro plan.</div>
        )}

        {d.eligible && d.status === "active" ? (
          <div className="mt-4">
            <div className="rounded-lg bg-[#F4F1E9] p-4 text-sm" data-testid="addon-breakdown">
              <div className="flex justify-between"><span className="text-slate-500">{d.base_plan_name} Plan</span><span className="text-[#16241F]">{money2(d.base_amount)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Plumbing Field Operations</span><span className="text-[#16241F]">{money2(d.addon_amount)}</span></div>
              <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 font-bold"><span className="text-[#16241F]">Total</span><span className="text-[#16241F]" data-testid="addon-total">{money2(d.total)}</span></div>
            </div>
            {d.next_billing_date && <p className="text-xs text-slate-400 mt-2">Next billing: {fmtDate(d.next_billing_date)}</p>}
            <Button variant="outline" className="mt-3 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={doCancel} data-testid="addon-cancel-btn">Cancel add-on</Button>
          </div>
        ) : d.eligible ? (
          <div className="mt-4">
            <div className="flex gap-2 mb-3">
              {["monthly", "annual"].map((c) => (
                <button key={c} onClick={() => setCycle(c)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${cycle === c ? "border-[#1E7A6B] bg-[#1E7A6B]/10 text-[#16241F]" : "border-slate-200 text-slate-500"}`} data-testid={`addon-cycle-${c}`}>{c === "monthly" ? "Monthly" : "Yearly"}</button>
              ))}
            </div>
            <div className="rounded-lg bg-[#F4F1E9] p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{d.base_plan_name} Plan</span><span className="text-[#16241F]">{money2(d.base_amount)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-500">Plumbing Field Operations</span><span className="text-[#16241F]">{money2(addonAmt)}</span></div>
              <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 font-bold"><span className="text-[#16241F]">Total</span><span className="text-[#16241F]">{money2(d.base_amount + addonAmt)}{per}</span></div>
            </div>
            <Button className="mt-3 text-white" style={{ background: TEAL }} onClick={subscribe} disabled={busy} data-testid="addon-unlock-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Unlock Plumbing Field Operations</>}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
